import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { html, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { getTickPositions, type Tick } from '../../core/ticks.js';
import { TimeAxisMath } from '../../core/time-axis-math.js';
import type { GanttZoomLevel } from '../../define.js';
import { tickLabelStyle, timeAxisHeaderStyle } from './time-axis-css.js';

/**
 * Renders the ticks visible in the current horizontal viewport. The
 * component is pure-presentational — positions + labels come from
 * {@link getTickPositions} and {@link TimeAxisMath}, which live in the
 * core directory so bar/dependency rendering can import the same math.
 *
 * Virtualisation (windowing the visible ticks based on scrollX / width)
 * is added by the virtual-scroll bead (sl-p9sb.5.1); for now the
 * caller is responsible for clipping the `[fromMs, toMs]` window.
 */
export class GanttTimeAxisHeader extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor zoom: GanttZoomLevel = 'week';

  @property({ attribute: false })
  accessor originMs!: number;

  @property({ attribute: false })
  accessor fromMs!: number;

  @property({ attribute: false })
  accessor toMs!: number;

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(timeAxisHeaderStyle);
  }

  override render(): TemplateResult {
    const math = new TimeAxisMath(this.zoom, this.originMs);
    const ticks: Tick[] = getTickPositions(this.fromMs, this.toMs, this.zoom);
    const totalWidth = math.dateToX(this.toMs) - math.dateToX(this.fromMs);

    return html`
      <div
        style=${styleMap({
          position: 'relative',
          width: `${Math.max(totalWidth, 1)}px`,
          height: '100%',
        })}
      >
        ${ticks.map(tick => {
          const x = math.dateToX(tick.dateMs) - math.dateToX(this.fromMs);
          return html`
            <div
              class=${tickLabelStyle}
              data-zoom=${this.zoom}
              style=${styleMap({ left: `${x}px` })}
            >
              ${tick.label}
            </div>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-time-axis-header': GanttTimeAxisHeader;
  }
}
